import { ChamferOutline } from "@/components/chamfer";
import { DashboardKpiCardHeader } from "@/components/dashboard/dashboard-stats/DashboardKpiCardHeader";
import { DashboardStafferTrendChart } from "@/components/dashboard/dashboard-stats/trend-and-funnel-charts/DashboardStafferTrendChart";
import { Card } from "@ui/card";
import { Skeleton } from "@ui/skeleton";

import { DASHBOARD_STAT_CARD_HEIGHT_CLASS } from "@/lib/constants";
import { dashboardKpis } from "@/lib/dashboardKpis";
import { formatting } from "@/lib/formatting";
import { cn } from "@/lib/utils";
import {
  DashboardComparisonMetric,
  DashboardKpiOverview,
  GoalPeriod,
  PerformanceTier,
  StafferStatKind,
} from "@/types";

const HEADER_BLOCK_PX = 62;

const METRIC_COPY: Record<
  DashboardComparisonMetric,
  { accentClassName: string; title: string }
> = {
  [DashboardComparisonMetric.TIME_TO_SCREEN]: {
    accentClassName: "border-t-lever-border-error",
    title: "Time to first screen • Last 5wks",
  },
  [DashboardComparisonMetric.SUBMITS]: {
    accentClassName: "border-t-lever-border-information",
    title: "Submits",
  },
  [DashboardComparisonMetric.REVENUE]: {
    accentClassName: "border-t-lever-border-success",
    title: "Revenue",
  },
};

export const DashboardGoalsOverTimeCard = ({
  goalsPeriod,
  isPending,
  metric,
  onMetricChange,
  onOpenScreenBreakdown,
  overview,
}: {
  goalsPeriod: GoalPeriod;
  isPending: boolean;
  metric: DashboardComparisonMetric;
  onMetricChange: (metric: DashboardComparisonMetric) => void;
  onOpenScreenBreakdown: (weekStart: string) => void;
  overview: DashboardKpiOverview | undefined;
}) => {
  /* Helpers */
  const copy = METRIC_COPY[metric];
  const rows = overview ? overview.trend : [];
  const goalFor = (goalMetric: DashboardComparisonMetric) =>
    dashboardKpis.goalFor({
      metric: goalMetric,
      overview,
      perStaffer: true,
      period: goalsPeriod,
    });

  const tierForGoal = (value: number, goal: number): PerformanceTier => {
    if (value >= goal) return PerformanceTier.GREEN;
    if (value >= goal / 2) return PerformanceTier.ORANGE;
    return PerformanceTier.RED;
  };

  /* Handlers */
  const handleNextMetric = () => {
    onMetricChange(dashboardKpis.nextMetric(metric));
  };

  /* Render */
  const renderChart = () => {
    if (isPending) return <Skeleton className="min-h-0 flex-1" />;
    if (metric === DashboardComparisonMetric.TIME_TO_SCREEN) {
      return (
        <DashboardStafferTrendChart
          chartTopPercent={dashboardKpis.cardTopPercent(HEADER_BLOCK_PX)}
          kind={StafferStatKind.TIME_TO_SCREEN}
          rows={rows}
          valueForRow={(row) => row.medianHoursToFirstScreen}
          onOpenScreenBreakdown={onOpenScreenBreakdown}
        />
      );
    }

    if (metric === DashboardComparisonMetric.SUBMITS) {
      const submitsGoal = goalFor(DashboardComparisonMetric.SUBMITS);
      return (
        <DashboardStafferTrendChart
          emptyLabel="No submits in the last five weeks"
          goalOverride={submitsGoal}
          isLowerBetter={false}
          kind={StafferStatKind.SEARCH_SUBMITS}
          rows={rows}
          tierForValue={(value) => tierForGoal(value, submitsGoal)}
          valueForRow={(row) => row.submits}
          onOpenScreenBreakdown={onOpenScreenBreakdown}
        />
      );
    }

    const revenueGoal = goalFor(DashboardComparisonMetric.REVENUE);
    return (
      <DashboardStafferTrendChart
        emptyLabel="No revenue in the last five weeks"
        formatValue={(value) =>
          value === null ? "—" : formatting.currency(value)
        }
        goalOverride={revenueGoal}
        isLowerBetter={false}
        kind={StafferStatKind.SEARCH_SUBMITS}
        rows={rows}
        tierForValue={(value) => tierForGoal(value, revenueGoal)}
        valueForRow={(row) => row.revenue}
        onOpenScreenBreakdown={onOpenScreenBreakdown}
      />
    );
  };

  const render = () => (
    <Card
      key={metric}
      className={cn(
        "lever-analysis-card flex min-h-0 w-full min-w-0 flex-col overflow-hidden !rounded-[var(--lever-radius-md)] border-lever-border-light bg-lever-surface-default px-4 pb-0 pt-3.5 font-lever-suisse shadow-none",
        DASHBOARD_STAT_CARD_HEIGHT_CLASS,
      )}
    >
      <ChamferOutline />
      <DashboardKpiCardHeader
        className="mb-2"
        eyebrow="Goals over time"
        onNext={handleNextMetric}
        title={copy.title}
      />
      {renderChart()}
    </Card>
  );

  return render();
};
